import Constants from '../Constants.js';
import RsrcReducer from './RsrcReducer.js';
import { getPath } from './utils/index.js';
import ActionCache from '../ActionCache.js';
// shamelessly stolen from resource users
/**
 * This is meant to be a generic, reusable user action generator.
 * It needs the ownerId.
 * <code>
 * user : ( userId ) => {
 *   return new User( {
 *     userId,
 *     ownerId : this.id, // with is th device, system, project, script, etc id
 *     resourceApi : ( dispatch, getState, opts, ownerId ) => {
 *       // this return's a the api on which the user's calls exist
 *       return opts.api.resource( ownerId ) 
 *     }
 *   })
 * }
 */

class Rsrc {
  static cache = new ActionCache();
  static setCacheTime(cacheTime) {
    Rsrc.cache.ttl = cacheTime;
  }
  static getCacheTime() {
    return Rsrc.cache.ttl;
  }
  constructor({
    ownerId,
    coownerId,
    path,
    resourceApi
  }) {
    this.resourceApi = resourceApi;
    this.ownerId = ownerId;
    this.coownerId = coownerId;
    this.path = getPath(path);
  }
  getResourceId() {
    const {
      ownerId,
      coownerId,
      path
    } = this;
    return `${ownerId}|${coownerId || ''}|${path || ''}`;
  }

  // caching adapted from InterfaceActions
  createAction(method, func, extra, reduxOpts = null) {
    return (dispatch, getState, opts) => {
      const promise = Rsrc.cache.get({
        method,
        ownerId: this.ownerId,
        coownerId: this.coownerId,
        path: this.path,
        extra
      });
      if (promise && reduxOpts?.noCache !== true) {
        return promise;
      }

      // override top level api with resource api
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const toCall = () => {
        dispatch({
          type: Constants.RSRC_STATUS,
          ownerId: this.ownerId,
          coownerId: this.coownerId,
          path: this.path,
          status: 'loading',
          queryName: reduxOpts?.queryName
        });
        return func(dispatch, getState, {
          ...opts,
          api
        }, this.ownerId, this.coownerId, this.path).catch(error => {
          dispatch({
            type: Constants.RSRC_STATUS,
            ownerId: this.ownerId,
            coownerId: this.coownerId,
            path: this.path,
            status: `${method}Error`,
            error,
            queryName: reduxOpts?.queryName
          });
          return Promise.reject(error);
        });
      };
      return Rsrc.cache.set(toCall, {
        method,
        ownerId: this.ownerId,
        coownerId: this.coownerId,
        path: this.path,
        extra
      }, {
        ttl: reduxOpts?.ttl
      });
    };
  }
  create(data, opts, reduxOpts = null) {
    return this.createAction('createRsrc', async (dispatch, getState, {
      api
    }, ownerId, coownerId, path) => {
      const rsrc = await api.resource(path).create(data, opts);
      dispatch({
        type: Constants.RSRC_CREATE,
        ownerId,
        coownerId,
        path: path || '',
        rsrc
      });
      dispatch({
        type: Constants.RSRC_STATUS,
        ownerId,
        coownerId,
        path,
        status: 'success',
        error: null
      });
      return rsrc;
    }, {
      data,
      opts
    }, reduxOpts);
  }
  canCreate() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const r = await api.resource(this.path).create(null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.getResourceId(),
        action: 'createRsrc',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  get(opts, reduxOpts = null) {
    return this.createAction('getRsrc', async (dispatch, getState, {
      api
    }, ownerId, coownerId, path) => {
      const rsrc = await api.resource(path).get(opts);
      dispatch({
        type: Constants.RSRC_GET,
        ownerId,
        coownerId,
        path,
        rsrc,
        options: opts,
        queryName: reduxOpts?.queryName
      });
      dispatch({
        type: Constants.RSRC_STATUS,
        ownerId,
        coownerId,
        path,
        status: 'success',
        error: null,
        queryName: reduxOpts?.queryName
      });
      return rsrc;
    }, null, reduxOpts);
  }
  canGet() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const r = await api.resource(this.path).get({
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.getResourceId(),
        action: 'getRsrc',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  update(patch, file = null, reduxOpts = null) {
    return this.createAction('updateRsrc', async (dispatch, getState, {
      api
    }, ownerId, coownerId, path) => {
      const rsrc = await api.resource(path).update(patch, file);
      dispatch({
        type: Constants.RSRC_UPDATE,
        ownerId,
        coownerId,
        path,
        rsrc,
        queryName: reduxOpts?.queryName
      });
      dispatch({
        type: Constants.RSRC_STATUS,
        ownerId,
        coownerId,
        path,
        status: 'success',
        error: null,
        queryName: reduxOpts?.queryName
      });
      return rsrc;
    }, {
      patch,
      file
    }, reduxOpts);
  }
  canUpdate() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const r = await api.resource(this.path).update(null, null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.getResourceId(),
        action: 'updateRsrc',
        allowed: r.allowed === true
      });
      return r;
    };
  }

  /**
   * userId: string or [ string ] 
   */
  delete(opts, reduxOpts = null) {
    return this.createAction('deleteRsrc', async (dispatch, getState, {
      api
    }, ownerId, coownerId, path) => {
      const res = await api.resource(path).delete(opts);
      dispatch({
        type: Constants.RSRC_DELETE,
        ownerId,
        coownerId,
        path,
        options: opts,
        queryName: reduxOpts?.queryName
      });
      dispatch({
        type: Constants.RSRC_STATUS,
        ownerId,
        coownerId,
        path,
        status: 'success',
        error: null,
        queryName: reduxOpts?.queryName
      });
      return res;
    }, {
      opts
    }, reduxOpts);
  }
  canDelete() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const r = await api.resource(this.path).delete({
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.getResourceId(),
        action: 'deleteRsrc',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  list(opts, reduxOpts = null) {
    return this.createAction('listRsrc', async (dispatch, getState, {
      api
    }, ownerId, coownerId, path) => {
      const rsrcs = await api.resource(path).list(opts);
      dispatch({
        type: Constants.RSRC_GET,
        ownerId,
        coownerId,
        path,
        rsrcs,
        options: opts,
        queryName: reduxOpts?.queryName
      });
      dispatch({
        type: Constants.RSRC_STATUS,
        ownerId,
        coownerId,
        path,
        status: 'success',
        error: null,
        queryName: reduxOpts?.queryName
      });
      return rsrcs;
    }, {
      opts
    }, reduxOpts);
  }
  canList() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.ownerId, this.coownerId, this.path);
      const r = await api.resource(this.path).list({
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.getResourceId(),
        action: 'listRsrc',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  getRsrc(state, reduxOpts) {
    return RsrcReducer.get(state, this.ownerId, this.coownerId, this.path, reduxOpts);
  }
  listRsrcs(state, reduxOpts) {
    return RsrcReducer.list(state, this.ownerId, this.coownerId, this.path, reduxOpts);
  }
  getStatus(state, reduxOpts) {
    return RsrcReducer.status(state, this.ownerId, this.coownerId, this.path, reduxOpts);
  }
}
export default Rsrc;